/** Helpers for TreeSize-style folder usage lists from RMM snapshots. */

/** PowerShell ConvertTo-Json often unwraps single-element arrays into objects. */
export function asArray(value) {
  if (value == null) return []
  return Array.isArray(value) ? value : [value]
}

function rowField(row, ...keys) {
  if (!row || typeof row !== 'object') return null
  for (const key of keys) {
    if (row[key] != null && row[key] !== '') return row[key]
  }
  return null
}

export function normaliseFolderRows(rows) {
  return asArray(rows)
    .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
    .map((row) => {
      const name = rowField(row, 'name', 'Name')
      const path = rowField(row, 'path', 'Path')
      return {
        name: name || path || '—',
        path: path || null,
        size_gb: (() => {
          const raw = rowField(row, 'size_gb', 'SizeGb', 'size_GB')
          if (raw == null) return null
          const n = Number(raw)
          return Number.isFinite(n) ? n : null
        })(),
        size_bytes: (() => {
          const raw = rowField(row, 'size_bytes', 'SizeBytes')
          if (raw == null) return null
          const n = Number(raw)
          return Number.isFinite(n) ? n : null
        })(),
        percent_of_used: (() => {
          const raw = rowField(row, 'percent_of_used', 'PercentOfUsed')
          if (raw == null) return null
          const n = Number(raw)
          return Number.isFinite(n) ? n : null
        })(),
        percent_of_total: (() => {
          const raw = rowField(row, 'percent_of_total', 'PercentOfTotal')
          if (raw == null) return null
          const n = Number(raw)
          return Number.isFinite(n) ? n : null
        })(),
        file_count: (() => {
          const raw = rowField(row, 'file_count', 'FileCount')
          if (raw == null) return null
          const n = Number(raw)
          return Number.isFinite(n) ? n : null
        })(),
      }
    })
    .filter((row) => row.name || row.path)
    .sort((a, b) => (b.size_gb ?? 0) - (a.size_gb ?? 0))
}

export function formatSizeGb(gb) {
  if (gb == null || Number.isNaN(Number(gb))) return '—'
  const n = Number(gb)
  if (!Number.isFinite(n)) return '—'
  if (n >= 100) return `${n.toFixed(0)} GB`
  if (n >= 10) return `${n.toFixed(1)} GB`
  return `${n.toFixed(2)} GB`
}

export function formatPercent(value) {
  if (value == null || Number.isNaN(Number(value))) return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `${n.toFixed(1)}%`
}

/** Retro block bar for monospace display (TreeSize-ish). */
export function folderUsageBar(percent, width = 20) {
  const safeWidth = Math.max(1, Math.min(40, Number(width) || 20))
  const raw = Number(percent)
  const safe = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 0
  const filled = Math.round((safe / 100) * safeWidth)
  return `${'█'.repeat(filled)}${'░'.repeat(Math.max(0, safeWidth - filled))}`
}

export function folderUsageFromDiskSnapshot(payload) {
  const data = payload || {}
  const usage = data.folder_usage
  if (!usage) return null
  const folders = normaliseFolderRows(usage.folders)
  const userProfiles = normaliseFolderRows(usage.user_profiles)
  return {
    drive: usage.drive || data.system_drive || null,
    used_gb: usage.used_gb ?? null,
    status: usage.status || (folders.length > 0 || userProfiles.length > 0 ? 'complete' : null),
    error: usage.error || null,
    folders,
    userProfiles,
    usersPath: usage.users_path || null,
    usersTotalGb: usage.users_total_gb ?? null,
    hasFolders: folders.length > 0,
    hasUserProfiles: userProfiles.length > 0,
  }
}

export function storageBreakdownFromExchangePayload(payload) {
  const sections = asArray(payload?.storage_breakdown)
  if (sections.length === 0) return []
  return sections
    .filter((section) => section && typeof section === 'object')
    .map((section, index) => {
      const folders = normaliseFolderRows(section.folders)
      const label =
        rowField(section, 'label', 'Label') ||
        rowField(section, 'path', 'Path') ||
        `Storage ${index + 1}`
      const path = rowField(section, 'path', 'Path')
      const totalRaw = rowField(section, 'total_gb', 'TotalGb', 'total_GB')
      const total_gb = totalRaw == null ? null : Number(totalRaw)
      return {
        id: `${label}-${path || index}`,
        label,
        path: path || null,
        total_gb: Number.isFinite(total_gb) ? total_gb : null,
        folders,
      }
    })
    .filter((section) => section.folders.length > 0 || section.path || section.total_gb != null)
}
